import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { PositionContextFormValues, usePositionContext } from '@/shared/context/PositionContext.tsx';
import { CustomProductsForms } from '@/shared/forms/CustomProductsForm/CustomProductsForm.tsx';
import { getCustomProductsDefaultValues } from '@/shared/forms/CustomProductsForm/domain/getCustomProductsDefaultValues.ts';
import { CustomProduct } from '@/shared/forms/CustomProductsForm/schemas/getCustomProductsFormSchema.ts';
import { getCommittedProductsFormRules } from '@/modules/position/domain/getCommittedProductsFormRules.ts';
import { usePositionV2 } from '@/clients/position/usePositionV2.ts';
import { CustomerInfo } from '@/shared/models/ContextDataModel/interfaces/entities/customer-info.ts';
import { BusinessCase } from '@schuettflix/interfaces';
import { ProductConfiguration } from '@/shared/models/ContextDataModel/interfaces/entities/product-configuration';
import { convertToProductConfigurations } from '@/shared/forms/CustomProductsForm/domain/convertToProductConfigurations.ts';

export type PositionProductSelectionProps = {
    onContinue: () => void;
    onClearSelection: () => void;
};

export const ManualPositionProductSelection = ({ onContinue, onClearSelection }: PositionProductSelectionProps) => {
    const { t } = useTranslation();
    const { contextData, isEditing, positionId, processingType } = usePositionContext();

    const formData = contextData.getFormData();
    const customerInfo = contextData.getCustomerInformation();
    const businessCase = contextData.getBusinessCase();
    const productConfigurations = contextData.getProductConfigurations();

    const handleSubmit = useCallback(
        (customProducts: CustomProduct[]) => {
            contextData.setProductConfigurations(convertToProductConfigurations(customProducts, processingType));
            onContinue();
        },
        [contextData, onContinue, processingType]
    );

    if (!businessCase || !customerInfo) return;

    if (isEditing) {
        return (
            <EditManualPositionProductSelection
                positionId={positionId}
                customerInfo={customerInfo}
                businessCase={businessCase}
                productConfigurations={productConfigurations}
                formData={formData}
                onSubmit={handleSubmit}
                onRemoveTemplate={onClearSelection}
            />
        );
    } else {
        return (
            <CustomProductsForms
                scope="POSITION"
                businessCase={businessCase}
                customerOrganizationId={customerInfo.organizationId}
                defaultValues={getCustomProductsDefaultValues(productConfigurations)}
                defaultBillingUnit={formData.billingUnit}
                onSubmit={handleSubmit}
                onClearSelection={onClearSelection}
                submitButtonLabel={t('product.productTemplates.toPositionSettingsBtn')}
                mode="create"
            />
        );
    }
};

const EditManualPositionProductSelection = ({
    positionId,
    formData,
    customerInfo,
    businessCase,
    productConfigurations,
    onSubmit,
    onRemoveTemplate,
}: {
    positionId: string;
    formData: PositionContextFormValues;
    customerInfo: CustomerInfo;
    businessCase: BusinessCase;
    productConfigurations?: ProductConfiguration[];
    onSubmit: (customProducts: CustomProduct[]) => void;
    onRemoveTemplate: () => void;
}) => {
    const { t } = useTranslation();
    const { data: position } = usePositionV2(positionId);

    return (
        <CustomProductsForms
            scope="POSITION"
            hideDeleteAllProductsButton={true}
            formRules={{
                customProducts: getCommittedProductsFormRules(position.productConfigurations, rule => ({
                    minQuantity: rule.salesQuantity.minValue,
                    unit: rule.salesQuantity.unit,
                    disableDelete: rule.disableDelete,
                    disableUnit: rule.disableSalesUnit,
                })),
            }}
            businessCase={businessCase}
            customerOrganizationId={customerInfo.organizationId}
            defaultValues={getCustomProductsDefaultValues(productConfigurations)}
            defaultBillingUnit={formData.billingUnit}
            onSubmit={onSubmit}
            onClearSelection={onRemoveTemplate}
            submitButtonLabel={t('product.productTemplates.toPositionSettingsBtn')}
            mode="edit"
        />
    );
};
